import React, { useState } from "react";
import Select from "react-select";
import "./TrialSerialNumberSelect.css";

const label = (value) => String(value || "").trim() || "Unknown";
const responsible = (machine) => String(machine.ResponsiblePerson || "").trim() || "Not assigned";

const TrialSerialNumberSelect = ({ machines, serialRef, selected = "", idPrefix, disabled = false,
  showFilters = true, selectFirst = false, emptyMessage = "No available machines match these filters." }) => {
  // Picker-only state: saving the event reads serialRef, never these filters.
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [responsibleFilter, setResponsibleFilter] = useState("");
  const [serial, setSerial] = useState(selected);
  const matches = (machine, nextBrand, nextModel, nextResponsible = "") =>
    (!nextBrand || label(machine.Brand) === nextBrand)
    && (!nextModel || label(machine.Model) === nextModel)
    && (!nextResponsible || responsible(machine) === nextResponsible);
  const brands = Array.from(new Set(machines.map((machine) => label(machine.Brand)))).sort();
  const models = Array.from(new Set(machines.filter((machine) => matches(machine, brand, ""))
    .map((machine) => label(machine.Model)))).sort();
  const responsibleNames = Array.from(new Set([
    ...machines.map(responsible), ...(responsibleFilter ? [responsibleFilter] : []),
  ])).sort();
  const filteredMachines = machines.filter((machine) => matches(machine, brand, model, responsibleFilter));
  const selectedMachine = filteredMachines.find((machine) => machine.SerialNumber === serial)
    || (selectFirst ? filteredMachines[0] : null);
  const options = filteredMachines.map((machine) => ({
    value: machine.SerialNumber,
    label: `${machine.SerialNumber} — ${label(machine.Model)} | Responsible: ${responsible(machine)}`,
    device: `${machine.SerialNumber} — ${label(machine.Model)}`,
    responsible: responsible(machine),
  }));
  const selectedOption = selectedMachine ? options.find((option) => option.value === selectedMachine.SerialNumber) : null;

  const changeFilters = (nextBrand, nextModel, nextResponsible = responsibleFilter) => {
    setBrand(nextBrand);
    setModel(nextModel);
    setResponsibleFilter(nextResponsible);
    const firstMatch = machines.find((machine) => matches(machine, nextBrand, nextModel, nextResponsible));
    setSerial((nextBrand || nextModel || nextResponsible) && firstMatch ? firstMatch.SerialNumber : "");
  };

  return <>
    {showFilters && <div className="row col-lg-12">
      <div className="form-group row col-lg-6">
        <label className="col-lg-4 col-form-label" htmlFor={`${idPrefix}-brand`}>Brand</label>
        <div className="col-lg-8">
          <select id={`${idPrefix}-brand`} className="form-control" value={brand} disabled={disabled}
            onChange={(event) => changeFilters(event.target.value, "")}>
            <option value="">All Brands</option>
            {brands.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </div>
      </div>
      <div className="form-group row col-lg-6">
        <label className="col-lg-4 col-form-label" htmlFor={`${idPrefix}-model`}>Model</label>
        <div className="col-lg-8 d-flex align-items-center">
          <select id={`${idPrefix}-model`} className="form-control" style={{ minWidth: 0 }} value={model} disabled={disabled}
            onChange={(event) => changeFilters(brand, event.target.value)}>
            <option value="">All Models</option>
            {models.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </div>
      </div>
    </div>}
    <div className="row col-lg-12">
      <div className="form-group row col-lg-12">
        <label className="col-lg-2 col-form-label" htmlFor={`${idPrefix}-responsible-filter`}>Responsible</label>
        <div className="col-lg-10 d-flex align-items-center">
          <select id={`${idPrefix}-responsible-filter`} className="form-control" style={{ minWidth: 0 }}
            value={responsibleFilter} disabled={disabled}
            onChange={(event) => changeFilters(brand, model, event.target.value)}>
            <option value="">All Responsible</option>
            {responsibleNames.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
          {(brand || model || responsibleFilter) && <button type="button" disabled={disabled}
            className="btn btn-light btn-sm ml-2 flex-shrink-0 text-nowrap"
            onClick={() => changeFilters("", "", "")}>Clear filters</button>}
        </div>
      </div>
    </div>
    <div className="row col-lg-12">
      <div className="form-group row col-lg-12">
        <label className="col-lg-2 col-form-label" htmlFor={`${idPrefix}-serial`}>Serial Number</label>
        <div className="col-lg-10">
          {/* The event save handlers continue to read a serial-only DOM value. */}
          <input type="hidden" ref={serialRef} value={selectedOption ? selectedOption.value : ""} disabled={disabled} />
          <Select
            inputId={`${idPrefix}-serial`}
            instanceId={`${idPrefix}-serial`}
            classNamePrefix="trial-serial-select"
            options={options}
            value={selectedOption}
            onChange={(option) => setSerial(option ? option.value : "")}
            isDisabled={disabled}
            isClearable={!selectFirst}
            placeholder="Please select"
            noOptionsMessage={() => emptyMessage}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 1060 }),
              singleValue: (base) => ({ ...base, width: "calc(100% - 8px)" }),
              control: (base) => ({ ...base, minHeight: 38, borderRadius: 4 }),
            }}
            formatOptionLabel={(option, { context }) => (
              <span className="trial-serial-option" data-serial={option.value} data-context={context}>
                <span className="trial-serial-option-device" title={option.device}>{option.device}</span>
                <span className="trial-serial-option-responsible" title={`Responsible: ${option.responsible}`}>
                  <span className="sr-only">Responsible: </span>{option.responsible}
                </span>
              </span>
            )}
          />
          {!filteredMachines.length && <small className="text-muted">{emptyMessage}</small>}
        </div>
      </div>
    </div>
  </>;
};

export default TrialSerialNumberSelect;
